import { ICalendarEventState } from '../state/calendar-event.state';
import * as Actions from '../actions/calendar-event.actions';
import * as WorkOrderActions from '../actions/work-order.actions';
import { createReducer, on } from '@ngrx/store';
import { createEntityAdapter, EntityAdapter } from '@ngrx/entity';
import { CalendarEvent } from '@app/core/interfaces';

const getCalendarEventEntityId = (calendarEvent: CalendarEvent): string => {
  const occurrenceStartTime = calendarEvent.occurrenceStartTime
    ? new Date(calendarEvent.occurrenceStartTime).toISOString()
    : null;

  if (occurrenceStartTime) {
    return `${calendarEvent.parentEventId ?? calendarEvent.id}:${occurrenceStartTime}`;
  }

  return String(calendarEvent.id);
};

export const adapter: EntityAdapter<CalendarEvent> =
  createEntityAdapter<CalendarEvent>({
    selectId: getCalendarEventEntityId,
  });

export const initialCalendarEventState: ICalendarEventState =
  adapter.getInitialState({
    selectedCalendarEvent: null,
    selectedCalendarEventId: null,
    loaded: false,
    loading: false,
    error: null,
    viewStartDate: null,
    viewEndDate: null,
  });

export const calendarEventReducers = createReducer(
  initialCalendarEventState,
  on(Actions.getCalendarEventsRequest, (state, { startDate, endDate }) => ({
    ...state,
    loading: true,
    error: null,
    viewStartDate: startDate.toISOString(),
    viewEndDate: endDate.toISOString(),
  })),
  on(Actions.getCalendarEventsSuccess, (state, { calendarEvents }) => {
    return adapter.setAll(calendarEvents, {
      ...state,
      loading: false,
      loaded: true,
      error: null,
    });
  }),
  on(Actions.getCalendarEventsFailed, (state, { error }) => ({
    ...state,
    loading: false,
    loaded: false,
    error,
  })),
  on(Actions.addCalendarEventRequest, (state, { eventData }) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(Actions.addCalendarEventSuccess, (state, { calendarEvent }) => {
    return adapter.addOne(calendarEvent, {
      ...state,
      loading: false,
      error: null,
    });
  }),
  on(Actions.addCalendarEventFailed, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(Actions.updateCalendarEventRequest, (state, { eventData }) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(Actions.updateCalendarEventSuccess, (state, { calendarEvent }) => {
    return adapter.upsertOne(calendarEvent, {
      ...state,
      loading: false,
      error: null,
    });
  }),
  on(Actions.updateCalendarEventFailed, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(Actions.removeCalendarEventRequest, (state, { id }) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(Actions.removeCalendarEventSuccess, (state, { id }) => {
    const idsToRemove = Object.keys(state.entities).filter((entityId) => {
      const entity = state.entities[entityId];
      return !!entity && (entity.id === id || entity.parentEventId === id);
    });

    return adapter.removeMany(idsToRemove, {
      ...state,
      loading: false,
      error: null,
    });
  }),
  on(Actions.removeCalendarEventFailed, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  // A deleted work order also deletes its calendar event server-side; drop it
  // from the store so the calendar/list updates without needing a refetch.
  on(WorkOrderActions.removeWorkOrderSuccess, (state, { id }) => {
    const idsToRemove = Object.keys(state.entities).filter((entityId) => {
      const entity = state.entities[entityId];
      return !!entity && entity.workOrderId === id;
    });
    return adapter.removeMany(idsToRemove, state);
  }),
);
